/**
 * Catálogo de modelos: proveedores, modelos y salud medida con tráfico real.
 *
 * Existe por un fallo que llegó a producción: la pestaña de Proveedores enseñaba
 * un guion en «URL base» y en «Variable de entorno de la clave» sobre los cuatro
 * proveedores configurados. Los datos estaban ahí; la vista leía
 * `provider.baseUrl` y la API devuelve la fila cruda de PostgreSQL, con
 * `base_url`. Un dato que existe y no se ve es peor que uno que falta: nadie va
 * a buscarlo a la base de datos porque el panel afirma que no hay nada.
 *
 * Por eso la normalización **acepta las dos formas** y no una: el día que el
 * backend serialice a camelCase, esta pantalla no puede volver a romperse en la
 * dirección contraria. Y es una lista blanca, como en `notifications.js`: la
 * respuesta son filas de tabla, y una columna que se añada mañana —una clave
 * cifrada, un campo interno— no entra en el panel por el mero hecho de existir.
 */

/** Campos que el panel usa de un proveedor. */
export const PROVIDER_FIELDS = Object.freeze([
  'id',
  'code',
  'name',
  'type',
  'baseUrl',
  'apiKeyEnv',
  'configured',
  'status',
])

/** Campos que el panel usa de un modelo del catálogo. */
export const MODEL_FIELDS = Object.freeze([
  'id',
  'code',
  'name',
  'provider',
  'providerCode',
  'contextWindow',
  'inputPricePerMillion',
  'outputPricePerMillion',
  'supportsTools',
  'supportsJson',
  'supportsVision',
  'status',
])

export const MODEL_HEALTH_STATUSES = Object.freeze(['healthy', 'degraded', 'down'])

/**
 * ¿Este proveedor funcionaría si se eligiera?
 *
 * La siembra inserta SIEMPRE los cuatro proveedores como catálogo de con quién
 * sabe hablar el sistema, así que los cuatro se veían idénticos aunque sólo uno
 * tuviera clave. «Compatible» y «utilizable» son cosas distintas y la pantalla
 * tiene que separarlas de un vistazo.
 *
 * Tres estados, no dos: sin el campo `configured` no se afirma nada. Y el que no
 * está configurado NO se pinta como una avería —ámbar, no rojo—, porque no lo
 * es: nadie ha cargado su clave todavía. Es la misma separación que hay entre un
 * correo que falló al enviarse y uno que no se ha podido ni intentar.
 */
const CONFIGURATION_DESCRIPTORS = Object.freeze({
  configured: { labelKey: 'platform.providerConfigured', tone: 'success', hintKey: null },
  missing: {
    labelKey: 'platform.providerNotConfigured',
    tone: 'warning',
    hintKey: 'platform.providerMissingKeyHint',
  },
  unknown: {
    labelKey: 'platform.providerConfigUnknown',
    tone: 'neutral',
    hintKey: 'platform.providerConfigUnknownHint',
  },
})

export const PROVIDER_CONFIGURATIONS = Object.freeze(Object.keys(CONFIGURATION_DESCRIPTORS))

/** @param {{ configured?: boolean|null }|null|undefined} provider */
export function describeProviderConfiguration(provider) {
  const value = provider?.configured
  const key = value === true ? 'configured' : value === false ? 'missing' : 'unknown'
  return { key, ...CONFIGURATION_DESCRIPTORS[key] }
}

const HEALTH_DESCRIPTORS = Object.freeze({
  healthy: { labelKey: 'platform.modelStatusHealthy', tone: 'success' },
  degraded: { labelKey: 'platform.modelStatusDegraded', tone: 'warning' },
  down: { labelKey: 'platform.modelStatusDown', tone: 'danger' },
  unknown: { labelKey: 'platform.modelStatusUnknown', tone: 'neutral' },
})

/**
 * Proveedor presentable.
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeProvider(raw) {
  const source = object(raw)
  return {
    // `code` como respaldo del identificador: sin una clave estable, `v-for`
    // reutiliza nodos entre proveedores distintos al refrescar.
    id: text(pick(source, 'id')) || text(pick(source, 'code')),
    code: text(pick(source, 'code')),
    name: text(pick(source, 'name')),
    type: text(pick(source, 'type')),
    baseUrl: nullable(pick(source, 'baseUrl', 'base_url')),
    apiKeyEnv: nullable(pick(source, 'apiKeyEnv', 'api_key_env')),
    // ¿Tiene valor la variable que nombra `apiKeyEnv`? Un booleano y NADA más:
    // ni el valor, ni su longitud, ni sus últimos caracteres. Una «pista» de una
    // clave es valor de la clave, y por eso aquí no hay respaldo a ningún
    // `apiKeyHint` — un campo así no debe existir y menos aún resucitarse.
    //
    // `null` cuando el campo no llega: un backend que todavía no lo emite no
    // puede hacer que el panel afirme que los cuatro proveedores están listos.
    // Es la misma regla que ya se aplicó al estado de salud de un modelo.
    configured: tristate(pick(source, 'configured')),
    status: text(pick(source, 'status')),
  }
}

/**
 * Modelo del catálogo.
 *
 * El nombre visible tiene tres orígenes posibles y el último recurso NO es el
 * identificador: un UUID en la columna «Modelo» no le dice nada a nadie, y era
 * lo que iba a salir en cuanto la tabla tuviera una fila.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeModel(raw) {
  const source = object(raw)
  const code = text(pick(source, 'code'))
  return {
    id: text(pick(source, 'id')) || code,
    code,
    name: text(pick(source, 'name', 'displayName', 'display_name')) || code,
    // El CÓDIGO del proveedor, aparte de su nombre visible. Es lo que permite
    // cruzar un modelo con su proveedor para saber si hay clave cargada: el
    // nombre visible («DeepSeek») no es una clave de unión fiable, el código
    // (`deepseek`) sí, y es lo único que manda `/platform/models`.
    providerCode: text(pick(source, 'providerCode', 'provider_code')) || text(pick(source, 'provider')),
    provider:
      text(pick(source, 'providerName', 'provider_name')) ||
      text(pick(source, 'providerCode', 'provider_code')) ||
      text(pick(source, 'provider')),
    contextWindow: number(pick(source, 'contextWindow', 'context_window')),
    // Los precios llegan como NUMERIC de PostgreSQL, es decir, como cadena.
    inputPricePerMillion: number(
      pick(source, 'inputPricePerMillion', 'input_cost_per_million', 'inputCostPerMillion'),
    ),
    outputPricePerMillion: number(
      pick(source, 'outputPricePerMillion', 'output_cost_per_million', 'outputCostPerMillion'),
    ),
    supportsTools: boolean(pick(source, 'supportsTools', 'supports_tools')),
    supportsJson: boolean(pick(source, 'supportsJson', 'supports_json')),
    supportsVision: boolean(pick(source, 'supportsVision', 'supports_vision')),
    status: text(pick(source, 'status')),
  }
}

/**
 * Salud de un modelo.
 *
 * Dos campos que el panel leía y la API no devuelve, y que por eso mentían:
 *
 *  - **la tasa de error** no viene calculada; vienen `errors` y `calls`. Leerla
 *    como ausente y pintar «0 %» convertía un modelo que falla siempre en uno
 *    impecable. Aquí se calcula con los dos datos que sí llegan.
 *  - **el estado** no viene en absoluto, y el panel caía en «Correcto» verde
 *    para todo. Sin dato se dice `unknown`; deducirlo de la tasa de error
 *    exigiría fijar unos umbrales que son política de plataforma y no del panel.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeModelHealth(raw) {
  const source = object(raw)
  const calls = number(pick(source, 'calls'))
  const errors = number(pick(source, 'errors'))
  const explicitRate = number(pick(source, 'errorRate', 'error_rate'))

  return {
    id: text(pick(source, 'id')),
    model: text(pick(source, 'model', 'code')) || text(pick(source, 'id')),
    provider: text(pick(source, 'provider', 'providerCode', 'provider_code')),
    calls,
    errors,
    p50LatencyMs: number(pick(source, 'p50LatencyMs', 'p50_latency_ms')),
    p95LatencyMs: number(pick(source, 'p95LatencyMs', 'p95_latency_ms')),
    errorRate:
      explicitRate ?? (calls !== null && calls > 0 && errors !== null ? errors / calls : null),
    status: normalizeHealthStatus(pick(source, 'status')),
  }
}

/**
 * De todo el catálogo, los modelos que hoy se pueden usar.
 *
 * El catálogo lista 33 modelos de siete proveedores porque describe con quién
 * SABE hablar Iris, no con quién PUEDE hablar. Con una sola clave cargada, esa
 * tabla es un escaparate de precios de cosas que no se pueden comprar: invita a
 * elegir un modelo que fallará en la primera llamada.
 *
 * Un modelo entra sólo si su proveedor dice `configured: true` de forma
 * EXPLÍCITA. Los otros dos estados quedan fuera a propósito:
 *
 *  - `false` es lo esperado: no hay clave, el modelo no funcionaría.
 *  - `null` —el backend no manda el campo— también, y esta es la decisión que
 *    importa: ante la duda no se afirma que algo funciona. Es la misma regla que
 *    ya rige `describeProviderConfiguration`, y prefiere una lista corta de más
 *    a una lista larga que miente. La pestaña de Proveedores sigue enseñando los
 *    cinco con su estado, así que el catálogo completo no se pierde de vista.
 *
 * El cruce es por código de proveedor y sin distinguir mayúsculas: `code` es
 * `citext` en PostgreSQL, así que `DeepSeek` y `deepseek` son la misma fila allí
 * y tienen que serlo aquí.
 *
 * @param {Array<ReturnType<typeof normalizeModel>>} models
 * @param {Array<ReturnType<typeof normalizeProvider>>} providers
 */
export function selectUsableModels(models = [], providers = []) {
  const configured = new Set(
    providers
      .filter((provider) => provider?.configured === true)
      .flatMap((provider) => [provider.code, provider.name])
      .filter(Boolean)
      .map((value) => String(value).toLowerCase()),
  )

  if (configured.size === 0) return []

  return models.filter((model) => {
    const candidates = [model?.providerCode, model?.provider].filter(Boolean)
    return candidates.some((value) => configured.has(String(value).toLowerCase()))
  })
}

/**
 * Motivos por los que filtrar el catálogo no deja NADA a la vista, con el texto
 * que corresponde a cada uno.
 *
 * Son tres situaciones distintas y sólo una de ellas permite afirmar que no hay
 * claves. Meterlas en un único mensaje sería repetir al revés el error que se
 * está corrigiendo: acusar de «no tienes ningún proveedor configurado» a una API
 * que sencillamente no informa de ello.
 */
const CATALOG_FALLBACK_REASONS = Object.freeze({
  /** Al menos un proveedor dice explícitamente que NO tiene clave, ninguno que sí. */
  noKeys: { noticeKey: 'platform.modelsNoProviderKeyNotice' },
  /**
   * Nadie afirma nada: el campo `configured` no llega, o la lista de proveedores
   * no se pudo cargar. No se sabe qué filtrar, así que no se filtra.
   */
  unknown: { noticeKey: 'platform.modelsConfigUnknownNotice' },
  /** Hay clave cargada, pero ningún modelo del catálogo es de ese proveedor. */
  noModelsForConfigured: { noticeKey: 'platform.modelsNoneForConfiguredNotice' },
})

export const CATALOG_FALLBACK_KEYS = Object.freeze(Object.keys(CATALOG_FALLBACK_REASONS))

/**
 * Qué enseñar hoy en la pestaña de Modelos.
 *
 * Por defecto, sólo los modelos que se pueden usar. Marcar las filas en lugar de
 * filtrarlas ya se probó y no resolvía la queja: con una sola clave cargada, 31
 * de las 33 filas eran ruido con precios que invitaban a elegirlas.
 *
 * Y una red de seguridad, porque el argumento en contra de filtrar era bueno: el
 * catálogo sale de `config/models.yml` y `configured` sale de la tabla
 * `providers`, así que si esa tabla no dijera nada, filtrar dejaría la pantalla
 * a cero delante de un catálogo lleno. Cuando el filtro no deja NADA se devuelve
 * un `fallback` con el motivo: la vista enseña el catálogo entero y dice por qué,
 * que es exactamente lo contrario de un vacío inexplicable.
 *
 * @param {Array<ReturnType<typeof normalizeModel>>} models
 * @param {Array<ReturnType<typeof normalizeProvider>>} providers
 */
export function describeModelAvailability(models = [], providers = []) {
  const usable = selectUsableModels(models, providers)
  const fallbackKey = usable.length === 0 && models.length > 0 ? fallbackReason(providers) : null

  return {
    usable,
    /** Cuántos modelos deja fuera el filtro, para poder ofrecerlos sin enseñarlos. */
    hiddenCount: models.length - usable.length,
    fallback: fallbackKey ? { key: fallbackKey, ...CATALOG_FALLBACK_REASONS[fallbackKey] } : null,
  }
}

/** @param {Array<{ configured?: boolean|null }>} providers */
function fallbackReason(providers) {
  const states = providers.map((provider) => provider?.configured)
  if (states.some((value) => value === true)) return 'noModelsForConfigured'
  if (states.some((value) => value === false)) return 'noKeys'
  return 'unknown'
}

/** @param {unknown} value */
export function normalizeHealthStatus(value) {
  return typeof value === 'string' && MODEL_HEALTH_STATUSES.includes(value) ? value : 'unknown'
}

/** @param {unknown} value */
export function describeModelHealth(value) {
  const key = normalizeHealthStatus(value)
  return { key, ...HEALTH_DESCRIPTORS[key] }
}

/* ─── Utilidades internas ─────────────────────────────────────────────────── */

/** Primer alias presente. Acepta camelCase y snake_case sin preferir ninguno. */
function pick(source, ...names) {
  for (const name of names) {
    if (source[name] !== undefined && source[name] !== null) return source[name]
  }
  return null
}

function object(value) {
  return value && typeof value === 'object' ? value : {}
}

function text(value) {
  if (typeof value === 'string') return value
  return typeof value === 'number' ? String(value) : ''
}

function nullable(value) {
  const result = text(value)
  return result === '' ? null : result
}

/** `null` cuando no hay dato, para poder distinguirlo de un cero real. */
function number(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function boolean(value) {
  return value === true || value === 'true' || value === 't' || value === 1
}

/**
 * Booleano de TRES estados: `true`, `false` y «no lo sé».
 *
 * `boolean()` no sirve para `configured`: convierte lo ausente en `false`, y
 * «no está configurado» y «el backend no lo dice» son cosas distintas que se
 * pintan distinto. Sólo un `true`/`false` explícito cuenta como respuesta.
 */
function tristate(value) {
  if (value === true || value === 'true' || value === 't') return true
  if (value === false || value === 'false' || value === 'f') return false
  return null
}
